// src/components/auditoria/AtividadesPage.js
import React, { Fragment, useState, useCallback, useMemo } from 'react';
import {
    Activity, Filter as FilterIcon, Loader2, XCircle, X,
    UserCheck, LogIn, LogOut, Edit, Trash2, Eye, PlusCircle,
    FileSignature, UserCog,
} from 'lucide-react';
import PageHeader from '../layout/PageHeader';
import Pagination from '../common/Pagination';
import { useAuditorias } from '../../hooks/useAuditorias';
import { useAuth } from '../../context/auth/AuthProvider';

// ✅ ACHADO: 'assinar' e 'impersonar' existem no enum de Audit.acao no
// backend (assinatura de contrato e uso de acesso de suporte/impersonate —
// ver server/models/Audit.js) mas nao tinham entrada aqui. Sem isso, essas
// atividades caiam no fallback generico de AcaoBadge (badge cinza, label
// cru 'assinar'/'impersonar') e nao apareciam no dropdown de filtro —
// justamente as duas acoes mais sensiveis de auditar.
const ACAO_CONFIG = {
    criar:      { label: 'Criar',      bgClass: 'bg-green-100 text-green-800',  Icon: PlusCircle },
    atualizar:  { label: 'Atualizar',  bgClass: 'bg-blue-100 text-blue-800',    Icon: Edit },
    excluir:    { label: 'Excluir',    bgClass: 'bg-red-100 text-red-800',      Icon: Trash2 },
    login:      { label: 'Login',      bgClass: 'bg-gray-100 text-gray-700',    Icon: LogIn },
    logout:     { label: 'Logout',     bgClass: 'bg-gray-100 text-gray-700',    Icon: LogOut },
    acesso:     { label: 'Acesso',     bgClass: 'bg-yellow-100 text-yellow-800', Icon: Eye },
    assinar:    { label: 'Assinar',    bgClass: 'bg-purple-100 text-purple-800', Icon: FileSignature },
    impersonar: { label: 'Acesso de Suporte', bgClass: 'bg-orange-100 text-orange-800', Icon: UserCog },
};

const ACOES_LISTA = Object.keys(ACAO_CONFIG);

const FILTROS_INICIAIS = {
    acao: '', entidade: '', usuario: '', page: 1, limit: 50,
};

const AcaoBadge = ({ acao }) => {
    const cfg = ACAO_CONFIG[acao] || { label: acao, bgClass: 'bg-gray-100 text-gray-700', Icon: Activity };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600,
        }} className={cfg.bgClass}>
            <cfg.Icon size={11} />
            {cfg.label}
        </span>
    );
};

const AuditoriaDetalheModal = ({ auditoria, onClose }) => {
    if (!auditoria) return null;
    const detalhes = auditoria.detalhes || {};
    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={onClose}>
            <div style={{
                background: '#fff', borderRadius: 12, padding: '1.5rem', maxWidth: 640, width: '95%',
                maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <AcaoBadge acao={auditoria.acao} />
                        <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{auditoria.entidade}</span>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
                        <X size={20}/>
                    </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                    {[
                        ['Entidade ID', auditoria.entidadeId],
                        ['IP', auditoria.ip],
                        ['Data', auditoria.createdAt ? new Date(auditoria.createdAt).toLocaleString('pt-BR') : '—'],
                        ['Resumo', auditoria.resumo],
                    ].map(([label, value]) => value ? (
                        <div key={label}>
                            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-secondary)', marginBottom: 2 }}>{label}</div>
                            <div style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>{String(value)}</div>
                        </div>
                    ) : null)}
                </div>

                {auditoria.usuario?.name && (
                    <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f9fafb', borderRadius: 8 }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-secondary)', marginBottom: 4 }}>Usuário</div>
                        <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{auditoria.usuario.name}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>{auditoria.usuario.email}</div>
                    </div>
                )}

                {auditoria.userAgent && (
                    <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-secondary)', marginBottom: 2 }}>User Agent</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', wordBreak: 'break-all' }}>{auditoria.userAgent}</div>
                    </div>
                )}

                {Object.keys(detalhes).length > 0 && (
                    <div>
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-secondary)', marginBottom: 6 }}>Detalhes da Ação</div>
                        <pre style={{
                            background: '#1e293b', color: '#e2e8f0', borderRadius: 8,
                            padding: '0.75rem', fontSize: '0.75rem', overflowX: 'auto',
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                            {JSON.stringify(detalhes, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        </div>
    );
};

const AtividadesPage = () => {
    const { user } = useAuth();
    const [showFilters, setShowFilters] = useState(false);
    const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
    const [activeFiltros, setActiveFiltros] = useState({ page: 1, limit: 50 });
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedAuditoria, setSelectedAuditoria] = useState(null);
    const itemsPerPage = 50;

    const queryParams = useMemo(() => ({
        ...activeFiltros,
        page: currentPage,
        limit: itemsPerPage,
    }), [activeFiltros, currentPage, itemsPerPage]);

    const { data: auditoriasData, isLoading } = useAuditorias(queryParams);

    const auditorias = auditoriasData?.data || [];
    const total = auditoriasData?.count || 0;

    const handleFiltroChange = useCallback((e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleAplicar = useCallback((e) => {
        if (e) e.preventDefault();
        const { page, limit, ...rest } = filtros;
        const cleaned = Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== ''));
        setActiveFiltros(cleaned);
        setCurrentPage(1);
    }, [filtros]);

    const handleLimpar = useCallback(() => {
        setFiltros(FILTROS_INICIAIS);
        setActiveFiltros({ page: 1, limit: 50 });
        setCurrentPage(1);
    }, []);

    const hasFilters = Object.entries(filtros).some(([k, v]) => !['page', 'limit'].includes(k) && v !== '');

    return (
        <Fragment>
            {selectedAuditoria && (
                <AuditoriaDetalheModal auditoria={selectedAuditoria} onClose={() => setSelectedAuditoria(null)} />
            )}

            <div className="page-management-container">
                <PageHeader
                    icon={<Activity />}
                    title="Atividades de Usuários"
                    subtitle={!isLoading && total > 0 ? `${total.toLocaleString('pt-BR')} registro${total !== 1 ? 's' : ''}` : null}
                    actions={<>
                        <button
                            onClick={() => setShowFilters(p => !p)}
                            className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                        >
                            <FilterIcon size={18} className="icon"/>
                            Filtros
                        </button>
                    </>}
                />

                {/* Painel de Filtros */}
                {showFilters && (
                    <div className="content-panel">
                        <form onSubmit={handleAplicar}>
                            <div className="form-grid-container-cols-2">
                                <div className="form-group">
                                    <label className="form-label">Ação</label>
                                    <select name="acao" value={filtros.acao} onChange={handleFiltroChange} className="form-input">
                                        <option value="">Todas</option>
                                        {ACOES_LISTA.map(a => (
                                            <option key={a} value={a}>{ACAO_CONFIG[a].label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Entidade</label>
                                    <input
                                        name="entidade"
                                        value={filtros.entidade}
                                        onChange={handleFiltroChange}
                                        className="form-input"
                                        placeholder="Ex: User, Agendamento, Contrato..."
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">ID do Usuário</label>
                                    <input
                                        name="usuario"
                                        value={filtros.usuario}
                                        onChange={handleFiltroChange}
                                        className="form-input"
                                        placeholder="ID do usuário"
                                    />
                                </div>
                            </div>
                            <div className="actions-bar" style={{ justifyContent: 'flex-end' }}>
                                {hasFilters && (
                                    <button type="button" onClick={handleLimpar} className="action-button action-button-secondary">
                                        <XCircle size={18} className="icon"/> Limpar
                                    </button>
                                )}
                                <button type="submit" className="action-button action-button-primary">
                                    <FilterIcon size={18} className="icon"/> Aplicar
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>

            <div className="table-container">
                <div className="table-wrapper">
                    {isLoading ? (
                        <div className="table-loading-state">
                            <Loader2 size={24} className="animate-spin"/>
                            <span>Carregando atividades...</span>
                        </div>
                    ) : auditorias.length > 0 ? (
                        <table className="styled-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 100 }}>Ação</th>
                                    <th style={{ textAlign: 'left' }}>Entidade</th>
                                    <th style={{ textAlign: 'left' }}>Usuário</th>
                                    <th>IP</th>
                                    <th>Data</th>
                                </tr>
                            </thead>
                            <tbody>
                                {auditorias.map(audit => (
                                    <tr
                                        key={audit._id}
                                        onClick={() => setSelectedAuditoria(audit)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td><AcaoBadge acao={audit.acao}/></td>
                                        <td style={{ textAlign: 'left' }}>
                                            <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{audit.entidade}</div>
                                            {audit.entidadeId && (
                                                <div style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)', fontFamily: 'monospace' }}>
                                                    {String(audit.entidadeId).slice(0, 20)}...
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'left' }}>
                                            {audit.usuario ? (
                                                <>
                                                    <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{audit.usuario.name || '—'}</div>
                                                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>{audit.usuario.email}</div>
                                                </>
                                            ) : (
                                                <span className="td-empty">—</span>
                                            )}
                                        </td>
                                        <td style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', fontFamily: 'monospace' }}>
                                            {audit.ip || '—'}
                                        </td>
                                        <td style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                                            {audit.createdAt ? new Date(audit.createdAt).toLocaleString('pt-BR') : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="table-empty-state">
                            <Activity size={48}/>
                            <h3>Nenhuma atividade encontrada</h3>
                            <p>{hasFilters ? 'Tente ajustar os filtros.' : 'Não há registros de atividade no momento.'}</p>
                        </div>
                    )}
                </div>
                <Pagination
                    totalItems={total}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                />
            </div>
        </Fragment>
    );
};

export default AtividadesPage;
